import { useEffect, useState } from 'react';
import { api, type Brand, type Connector, type PlatformReport } from '../lib/api';

// The platform list used to be written here, again — a third copy beside the
// connectors route's and the publisher's, and the one the buttons were drawn
// from. It comes from the API now, which reads it from the same registry the
// publisher dispatches on, so a destination cannot be offered that the worker
// would refuse.
/**
 * What each connector state means to somebody looking at the table.
 *
 * `status` was fetched and never rendered. Expired and revoked both still have
 * a stored token, so the old has_token check called them connected — and then
 * the publish failed for reasons the screen had already claimed were fine.
 */
const STATE: Record<Connector['status'], { label: string; tone: string; hint?: string }> = {
  connected: { label: 'connected', tone: 'var(--green)' },
  expired:   { label: 'needs reconnecting', tone: 'var(--amber)',
               hint: 'the stored permission has lapsed — publishing will fail until it is renewed' },
  revoked:   { label: 'access revoked', tone: 'var(--red)',
               hint: 'the account withdrew permission, usually from the platform’s own settings' },
  error:     { label: 'erroring', tone: 'var(--red)',
               hint: 'the last attempt failed — see the reason below' },
};

/**
 * Why a destination cannot be connected, for the person who can fix it.
 *
 * Two different answers that used to read as one. "Not configured" is an
 * operator's job and is finishable today; "publishing not built yet" is ours and
 * no amount of configuration changes it. Telling an admin to go and create a
 * TikTok client, when a TikTok client would not make TikTok publish, is sending
 * them to do work that cannot help.
 *
 * The variable names come from the API rather than being restated here, so
 * adding a platform never leaves this list behind.
 */
function Why({ report }: { report: PlatformReport | null }) {
  if (!report) return <span className="chip">unknown platform</span>;
  if (!report.implemented) {
    return (
      <span className="chip" title="Credentials will not change this — the uploader does not exist yet.">
        publishing not built yet
      </span>
    );
  }
  return (
    <span className="chip" title={`Set ${report.missing.join(' and ')} on the api service.`}>
      needs {report.missing.length ? report.missing.join(' + ') : 'configuration'}
    </span>
  );
}

export function Connectors({ brand }: { brand: Brand | null }) {
  const [rows, setRows] = useState<Connector[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [platform, setPlatform] = useState<string>('youtube');
  // What this deployment can do, per platform, as the API reports it. Starts
  // EMPTY rather than optimistic, so nothing offers to connect while the answer
  // is unknown or if the check itself fails — the same default as the
  // password-reset entrance, for the same reason.
  const [platforms, setPlatforms] = useState<PlatformReport[]>([]);
  const reportFor = (name: string) => platforms.find((p) => p.platform === name) ?? null;

  async function load() {
    try {
      // One call, not two. /v1/connectors carries the platform reports as well
      // as the rows, and it is AUTHENTICATED — so unlike /v1/capabilities it
      // includes `missing`, the credential variables this deployment has not
      // set. This page was calling both and throwing away the half with the
      // useful half in it.
      const r = await api.connectors();
      setRows(r.connectors);
      setPlatforms(r.platforms ?? []);
    } catch (e) { setError((e as Error).message); }
  }
  useEffect(() => { void load(); }, []);
  // Coming back from Google leaves the studio untouched, so the list is stale
  // until something reloads it. Refresh when the tab regains focus rather than
  // polling forever.
  useEffect(() => {
    const onFocus = () => void load();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  async function add() {
    if (!brand) { setError('choose a brand first'); return; }
    setBusy('add'); setError(null);
    try { await api.addConnector(brand.id, platform); await load(); }
    catch (e) { setError((e as Error).message); }
    finally { setBusy(null); }
  }

  // DELETE /v1/connectors/:id has existed since the connector table did and had
  // no caller — you could add a destination and never remove one. It destroys
  // the encrypted refresh token, so the confirmation says the part that
  // actually costs something: reconnecting needs a fresh consent, and Google
  // frequently withholds a refresh token on a repeat grant.
  async function remove(c: Connector) {
    const warning = c.has_token
      ? `Remove ${c.platform}${c.account_handle ? ` (${c.account_handle})` : ''}?\n\n`
        + 'This deletes the stored permission. Reconnecting means signing in to the '
        + 'platform again, and it does not always hand back a new token on a second grant.'
      : `Remove ${c.platform}? It is not connected, so nothing is lost.`;
    if (!window.confirm(warning)) return;
    setBusy(c.id); setError(null); setNote(null);
    try {
      await api.removeConnector(c.id);
      setNote(`${c.platform} removed.`);
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(null); }
  }

  async function connect(c: Connector) {
    setBusy(c.id); setError(null); setNote(null);
    try {
      const { url } = await api.authorizeYouTube(c.id);
      // A new tab, not a redirect: losing the studio's state to an OAuth round
      // trip is a worse experience than an extra tab.
      //
      // navigation-ok: this is Google's consent screen, not a signed URL. The
      // whole point is to send the person to accounts.google.com, and there is
      // no credential of ours in it — the `state` is a signed token that is
      // useless without Google completing the round trip. Every OTHER
      // navigation in this app is checked by the CI guard of the same name.
      window.open(url, '_blank', 'noopener');
      setNote('Approve the permissions in the tab that just opened, then come back here — this list refreshes when you return.');
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(null); }
  }

  return (
    <>
      <div className="vhead" data-move="destination-broken">
        <div>
          <h2>Connectors</h2>
          <p>
            Where finished cuts go. A destination can be added before it is connected —
            the publisher waits for the account rather than failing the publish.
          </p>
        </div>
      </div>

      {/* Says why the buttons are inert. A disabled control with no explanation
          is worse than the 503 it replaces: the error at least told you
          something had gone wrong. */}
      {platforms.length > 0 && !platforms.some((p) => p.ready) && (
        <div className="notice">
          No platform can be connected on this deployment yet. You can still add
          destinations and schedule to them; the publisher waits for the account
          rather than failing the publish.
          {' '}See <code>docs/connecting-platforms.md</code> for what each one needs.
        </div>
      )}

      {error && <div className="err">{error}</div>}
      {note && <div className="err" style={{ borderColor: 'var(--green)', color: 'var(--green)' }}>{note}</div>}

      <div className="panel">
        <div className="panel-hd"><h3>Add a destination</h3></div>
        <div className="panel-bd">
          <div className="row wrap">
            <select value={platform} onChange={(e) => setPlatform(e.target.value)}>
              {platforms.map((p) => (
                <option key={p.platform} value={p.platform}>
                  {p.ready ? p.label : `${p.label} — cannot publish yet`}
                </option>
              ))}
            </select>
            <button className="btn pri" disabled={busy === 'add' || !brand} onClick={() => void add()}>
              {busy === 'add' ? 'Adding…' : `Add to ${brand?.name ?? 'a brand'}`}
            </button>
          </div>
        </div>
      </div>

      <div className="panel" style={{ marginTop: 16 }}>
        <div className="panel-hd"><h3>Destinations</h3><span className="chip">{rows.length}</span></div>
        {rows.length === 0 ? (
          <div className="empty">No destinations yet.</div>
        ) : (
          <table>
            <thead><tr><th>Platform</th><th>Account</th><th>Status</th><th /></tr></thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id}>
                  <td><b>{c.platform}</b><div className="sub-dim">{c.brand_name}</div></td>
                  <td style={{ color: 'var(--ink-2)' }}>{c.account_handle ?? '—'}</td>
                  {/* The row used to read has_token ? 'connected' : 'not
                      connected', which ignored status entirely — so a channel
                      whose token had EXPIRED or been REVOKED still said
                      "connected", and the publish that then failed looked
                      inexplicable. The token existing and the token working are
                      different facts. */}
                  <td style={{ color: STATE[c.status]?.tone ?? 'var(--amber)', fontWeight: 500 }}>
                    {c.has_token ? (STATE[c.status]?.label ?? c.status) : 'not connected'}
                    {c.has_token && STATE[c.status]?.hint && (
                      <div className="sub-dim">{STATE[c.status].hint}</div>
                    )}
                    {c.last_error && <div className="sub-dim" style={{ color: 'var(--red)' }}>{c.last_error}</div>}
                  </td>
                  <td className="r">
                    <button
                      className="btn"
                      style={{ marginRight: 8 }}
                      disabled={busy === c.id}
                      onClick={() => void remove(c)}
                    >
                      Remove
                    </button>
                    {/* One flag, from the API, rather than a local set of
                        connectable names and a separate boolean about YouTube.
                        When it is false the chip says which half is missing —
                        a disabled control with no explanation is worse than the
                        error it replaces. */}
                    {c.has_token ? (
                      <button className="btn" disabled={busy === c.id || !reportFor(c.platform)?.ready}
                              onClick={() => void connect(c)}>
                        Reconnect
                      </button>
                    ) : reportFor(c.platform)?.ready ? (
                      <button className="btn pri" disabled={busy === c.id}
                              onClick={() => void connect(c)}>
                        {busy === c.id ? 'Opening…' : 'Connect'}
                      </button>
                    ) : (
                      <Why report={reportFor(c.platform)} />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
